import { JSX, ReactNode, useContext, useMemo } from "react";

import { Context } from "../../models/context";
import { BaseScopes, GroupScopes, Scope } from "../../models/scopes";

interface PermissionGuardProps {
    requiredScopes?: Scope[];
    requiredGroupScopes?: GroupScopes[];
    groupId?: string;
    alt?: JSX.Element;
    children: ReactNode;
}

export default function PermissionGuard({ requiredScopes, requiredGroupScopes, groupId, alt, children }: PermissionGuardProps): JSX.Element | ReactNode {
    const context = useContext(Context);

    const hasRequiredScopes: boolean = useMemo(() => {
        if (!requiredScopes) return true; // no scopes required
        if (context.user?.permissions.has(BaseScopes.ADMIN) ?? false) return true; // user is admin
        for (const scope of requiredScopes || []) {
            if (context.user?.permissions?.has(scope)) {
                return true;
            }
        }
        return false;
    }, [context.user, context.user?.permissions, requiredScopes]);

    const hasRequiredGroups: boolean = useMemo(() => {
        if (!requiredGroupScopes || requiredGroupScopes.length === 0) return true; // no groups required
        if (context.user?.permissions.has(BaseScopes.ADMIN)) return true; // user is admin
        if (!groupId) {
            console.warn("PermissionGuard: requiredGroupScopes provided without groupId. This will always fail.");
            return false;
        }
        for (const groupScope of requiredGroupScopes) {
            if (context.user?.groupPermissions?.[groupId]?.has(groupScope)) {
                return true;
            }
        }
        return false;
    }, [requiredGroupScopes, groupId, context.user?.groupPermissions, context.user?.permissions]);

    return hasRequiredGroups && hasRequiredScopes ? children : alt ?? <></>
}